import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getOwnerStats, getMyListings, type OwnerStats } from "@/services/ownerService";
import { getShareCount } from "@/services/shareService";
import { isPremiumPlan } from "@/utils/plans";

const STATUS_LABEL: Record<string, string> = {
  active:    "Live",
  pending:   "In review",
  rejected:  "Needs changes",
  suspended: "Suspended",
};

const STATUS_CLASS: Record<string, string> = {
  active:    "bg-emerald-100 text-emerald-700",
  pending:   "bg-amber-100 text-amber-700",
  rejected:  "bg-rose-100 text-rose-700",
  suspended: "bg-background-200 text-foreground-600",
};

function fmt(n: number): string {
  return (n ?? 0).toLocaleString("en-IN");
}

export default function ListingPerformance() {
  const { accessToken } = useAuth();
  const [stats, setStats] = useState<OwnerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [shareCounts, setShareCounts] = useState<Record<string, number>>({});
  const [videoInfo, setVideoInfo] = useState<Record<string, { premium: boolean; hasVideo: boolean }>>({});

  useEffect(() => {
    let active = true;
    getOwnerStats(accessToken)
      .then((res) => { if (active) setStats(res); })
      .catch(() => { /* keep null → empty state */ })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [accessToken]);

  // Per-listing share totals (each row shows its own public counter).
  useEffect(() => {
    const rows = stats?.listings ?? [];
    if (rows.length === 0) return undefined;
    let active = true;
    Promise.all(
      rows.map((row) =>
        getShareCount("business", row.slug)
          .then((count) => [row.slug, count] as const)
          .catch(() => [row.slug, 0] as const),
      ),
    )
      .then((entries) => {
        if (active) setShareCounts(Object.fromEntries(entries));
      })
      .catch(() => { /* counters are non-critical */ });
    return () => { active = false; };
  }, [stats]);

  // Premium listings: flag whether a Video tour link has been added.
  useEffect(() => {
    let active = true;
    getMyListings(accessToken)
      .then((list) => {
        if (!active) return;
        const map: Record<string, { premium: boolean; hasVideo: boolean }> = {};
        list.forEach((l) => {
          map[l.slug] = { premium: isPremiumPlan(l.planSlug), hasVideo: Boolean(l.videoUrl) };
        });
        setVideoInfo(map);
      })
      .catch(() => { /* chip is non-critical */ });
    return () => { active = false; };
  }, [accessToken]);

  const totals = stats?.totals ?? { views: 0, leads: 0, reviews: 0 };
  const rows = stats?.listings ?? [];
  const maxViews = Math.max(...rows.map((r) => r.views), 1);

  const summary = [
    { label: "Profile views", value: fmt(totals.views),   icon: "ri-eye-line",      color: "text-primary-600",   bg: "bg-primary-50" },
    { label: "Total enquiries", value: fmt(totals.leads),  icon: "ri-mail-line",     color: "text-accent-600",    bg: "bg-accent-50" },
    { label: "Total reviews", value: fmt(totals.reviews),  icon: "ri-star-line",     color: "text-secondary-600", bg: "bg-secondary-50" },
  ];

  return (
    <div className="rounded-xl border border-background-200 bg-background-50">
      <div className="flex items-center justify-between border-b border-background-200 px-6 py-4">
        <div>
          <h2 className="font-heading text-base font-bold text-foreground-950">Listing performance</h2>
          <p className="mt-0.5 text-xs text-foreground-500">Views, enquiries and reviews across your listings.</p>
        </div>
        <Link
          to="/listings"
          className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-primary-600 transition-colors hover:text-primary-700"
        >
          Manage <i className="ri-arrow-right-line text-sm" />
        </Link>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 divide-x divide-background-100 border-b border-background-200">
        {summary.map((s) => (
          <div key={s.label} className="px-6 py-4">
            <div className="flex items-center gap-2">
              <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${s.bg}`}>
                <i className={`${s.icon} text-sm ${s.color}`} />
              </span>
              <span className="text-xs text-foreground-500">{s.label}</span>
            </div>
            <p className="mt-2 font-heading text-xl font-bold text-foreground-950 tabular-nums">
              {loading ? "—" : s.value}
            </p>
          </div>
        ))}
      </div>

      {/* Per-listing rows */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-sm font-medium text-foreground-700">No listing activity yet</p>
          <p className="mt-1 text-xs text-foreground-500">Add a business to start tracking views and enquiries.</p>
        </div>
      ) : (
        <div className="divide-y divide-background-100">
          {rows.map((row) => (
            <div key={row.id} className="px-6 py-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-foreground-900">{row.name}</p>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_CLASS[row.status] ?? STATUS_CLASS.suspended}`}>
                    {STATUS_LABEL[row.status] ?? row.status}
                  </span>
                  {videoInfo[row.slug]?.premium && (
                    videoInfo[row.slug]?.hasVideo ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                        <i className="ri-video-line text-[11px]" /> Video tour added
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                        <i className="ri-video-line text-[11px]" /> Video tour missing
                      </span>
                    )
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-eye-line text-sm text-foreground-400" />
                    <span className="font-semibold text-foreground-800 tabular-nums">{fmt(row.views)}</span> views
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-mail-line text-sm text-foreground-400" />
                    <span className="font-semibold text-foreground-800 tabular-nums">{fmt(row.leads)}</span> enquiries
                    {row.unreadLeads > 0 && (
                      <span className="rounded-full bg-primary-100 px-1.5 py-0.5 text-[10px] font-bold text-primary-700">
                        {row.unreadLeads} new
                      </span>
                    )}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-star-fill text-[11px] text-accent-500" />
                    <span className="font-semibold text-foreground-800 tabular-nums">
                      {row.rating > 0 ? row.rating.toFixed(1) : "—"}
                    </span>
                    ({fmt(row.reviews)})
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-share-forward-line text-sm text-foreground-400" />
                    <span className="font-semibold text-foreground-800 tabular-nums">
                      {shareCounts[row.slug] === undefined ? "—" : fmt(shareCounts[row.slug])}
                    </span>
                    {shareCounts[row.slug] === 1 ? "share" : "shares"}
                  </span>
                </div>
              </div>
              <div className="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
                <div
                  className="h-1.5 rounded-full bg-primary-500 transition-all duration-500"
                  style={{ width: `${Math.max(4, Math.round((row.views / maxViews) * 100))}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}